import { Event, List } from '@mui/icons-material'
import { ToggleButton, ToggleButtonGroup } from '@mui/material'
import { styled } from '@mui/material/styles'
import { addMinutes, roundToNearestMinutes } from 'date-fns'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useParams } from 'react-router-dom'
import { AvailabilityType, Country, EventType, Permission, RouteParameters } from '../../../constants'
import { useAuth, useCountry, useEventTracking, useFeatureFlags, useResource } from '../../../hooks'
import { theme } from '../../../modules/App/Providers/Theme/theme'
import { ConstraintModal } from '../ConstraintModal'
import { DtoCalendarConstraint } from '../types'
import { ResourceList } from '../UserList'
import { Actions } from './Actions'

const StyledLink = styled(Link)`
  &.Mui-selected {
    background: ${theme.palette.grey[200]} !important;
    border-color: ${theme.palette.primary.main} !important;
    color: ${theme.palette.primary.main} !important;
  }
`
// <Calendar /> places a default 'calendar' in routeParameter :resourceId when the resourceId is empty.
const EMPTY_ROUTE_PARAMETER = 'calendar'
const WEEKLY_MONDAY_THRU_FRIDAY = 'FREQ=WEEKLY;INTERVAL=1;WKST=MO;BYDAY=MO,TU,WE,TH,FR'

const baseConstraint: DtoCalendarConstraint = {
  appointmentTypes: [],
  availabilityType: AvailabilityType.AVAILABLE,
  dateEnd: new Date(),
  dateEndTimeZone: '',
  isAllDay: false,
  dateStart: new Date(),
  dateStartTimeZone: '',
  recurrenceFrequency: WEEKLY_MONDAY_THRU_FRIDAY,
  subject: '',
}

const getBaseConstraint = (country: Country) => {
  if (country === Country.DE) return { ...baseConstraint, recurrenceFrequency: '' }
  return baseConstraint
}

const Navigation = () => {
  const location = useLocation()
  const { user } = useAuth()
  const country = useCountry()
  const { t } = useTranslation()
  const { resource } = useResource()
  const route = location.pathname.split('/').reverse()[0]
  const [isEditorOpen, beEditorOpen] = useState(false)
  const [constraint, setConstraint] = useState<DtoCalendarConstraint>(getBaseConstraint(country))
  const [isShopScheduleManagementFeatureFlagEnabled] = useFeatureFlags<boolean>(['ENABLE_SHOP_SCHEDULE_MANAGEMENT'])
  const { track: trackEvent } = useEventTracking()
  const { [RouteParameters.SHOP_ID]: shopId, [RouteParameters.RESOURCE_ID]: resourceId } = useParams()
  const [hasResourceId, setHasResourceId] = useState(false)

  const onAddConstraintClick = (availabilityType: AvailabilityType) => {
    const now = roundToNearestMinutes(new Date(), { nearestTo: 30 })

    setConstraint({
      ...getBaseConstraint(country),
      availabilityType: availabilityType,
      dateEnd: addMinutes(now, 30),
      dateEndTimeZone: resource?.timezone as string,
      dateStart: now,
      dateStartTimeZone: resource?.timezone as string,
    })

    beEditorOpen(true)
  }

  const userPermissions = user?.permissions ?? new Set<string>()
  const useShopScheduling = Boolean(shopId)
  const hasUserListVisibilityRole =
    useShopScheduling &&
    userPermissions.has(Permission.AVAILABILITY_CONSTRAINTS_DELETE) &&
    userPermissions.has(Permission.AVAILABILITY_CONSTRAINTS_WRITE)
  const shouldSeeResourceList = hasUserListVisibilityRole && isShopScheduleManagementFeatureFlagEnabled
  const gridTemplateColumns = shouldSeeResourceList ? 'auto 1fr 1fr' : 'auto 1fr'

  useEffect(() => {
    if (shouldSeeResourceList) {
      trackEvent(EventType.SHOP_CALENDAR_PAGE_VIEWED, {
        data: { eventName: EventType.SHOP_CALENDAR_PAGE_VIEWED, shopId, userRoles: user?.roles },
      })
      if (resourceId && resourceId !== EMPTY_ROUTE_PARAMETER) {
        trackEvent(EventType.SHOP_CALENDAR_RESOURCE_SELECTED, { data: { resourceIds: [resourceId], shopId, userRoles: user?.roles } })
      }
    }
    setHasResourceId(Boolean(resourceId && resourceId !== EMPTY_ROUTE_PARAMETER))
  }, [shouldSeeResourceList, resourceId, shopId])

  const Container = styled('div')`
    align-items: center;
    display: grid;
    gap: 0;
    grid-template-columns: ${gridTemplateColumns};
    grid-template-rows: 1fr;
    place-self: start;
    width: calc(100vw - 3rem);
  `

  let opacity = '1'
  if (shouldSeeResourceList) {
    opacity = hasResourceId ? '1' : '.5'
  }

  return (
    <>
      <Container>
        {shouldSeeResourceList && <ResourceList shopId={shopId} resourceId={resourceId} />}
        <ToggleButtonGroup size="small" value={route.toLowerCase()}>
          <ToggleButton
            style={{ opacity }}
            disabled={!hasResourceId && shouldSeeResourceList}
            component={StyledLink}
            to="calendar"
            value="calendar"
          >
            <Event />
            &nbsp;{t('pages.settings.resources.availabilityConstraints.button.calenderView')}
          </ToggleButton>
          <ToggleButton
            style={{ opacity }}
            disabled={!hasResourceId && shouldSeeResourceList}
            component={StyledLink}
            to="list"
            value="list"
          >
            <List />
            &nbsp;{t('pages.settings.resources.availabilityConstraints.button.listView')}
          </ToggleButton>
        </ToggleButtonGroup>
        <Actions
          disabled={!hasResourceId && shouldSeeResourceList}
          onAddAvailableTimeClick={() => onAddConstraintClick(AvailabilityType.AVAILABLE)}
          onAddBlockedTimeClick={() => onAddConstraintClick(AvailabilityType.UNAVAILABLE)}
        />
      </Container>
      <ConstraintModal constraint={constraint} isNew isOpen={isEditorOpen} onClose={() => beEditorOpen(false)} />
    </>
  )
}

export { Navigation }
